// BotLeads.tsx — the Leads screen (spec D4/M5 task-3, handoff §9). Header row with a
// count + outline "Export CSV" link, table per @repo/ui table spec, "Load more" keyset
// pagination (same `useInfiniteQuery` shape as `BotConversations`).
//
// Deletion (hygiene-cycle spec §B) — a live compliance gap against the Privacy page's
// deletion promise: per-row delete (simple confirm naming the lead's email — a single lead
// isn't the destructive-enough action to warrant type-to-confirm) + "Delete all leads" in
// the header (type-to-confirm on the bot's name, same friction as BotSettings' DangerZone
// bot-delete dialog, since wiping every lead for a bot IS that destructive).
//
// Triage-state design (2026-08-21) Task 5 §C — filter segmented control `New (n) · Contacted
// · Archived · All` (default New when there's something new, else All), a status chip per
// row, row actions (Mark contacted / Archive / Mark new — the two that change state from
// wherever the row currently is), and a checkbox column + bulk bar wired to the bulk-status
// endpoint. `useEffect`-free default: `filter` is `filterOverride` once the owner has clicked
// a tab, else derived fresh from `overview.totals.newLeads` every render — the moment the
// owner explicitly picks a tab, that pick sticks even if `newLeads` changes under them.
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InboxIcon, Trash2Icon } from 'lucide-react';
import { Badge } from '@repo/ui/components/badge';
import { Button, buttonVariants } from '@repo/ui/components/button';
import { EmptyState } from '@repo/ui/components/empty-state';
import { Input } from '@repo/ui/components/input';
import { SkeletonText } from '@repo/ui/components/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import type { BotDto, LeadListItemDto, LeadStatus } from '@repo/shared-types';
import { PageHeader } from '../components/PageHeader';
import { type LeadsFilter, useBulkSetLeadStatus, useLeads, useSetLeadStatus } from '../hooks/useLeads';
import { useBotOverview } from '../hooks/useBotOverview';
import { apiFetch, withWorkspaceHeader } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

/** Table shape at the real 44px row height (handoff Shell Anatomy §06). */
function TableSkeleton() {
  const widths = ['w-[80%]', 'w-[64%]', 'w-[72%]', 'w-[56%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-4 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

function filterTabs(t: Catalog): { id: LeadsFilter; label: string }[] {
  return [
    { id: 'new', label: t.leads.filterNew },
    { id: 'contacted', label: t.leads.filterContacted },
    { id: 'archived', label: t.leads.filterArchived },
    { id: 'all', label: t.leads.filterAll },
  ];
}

/** Every OTHER status than the row's current one, in a fixed order — a row's actions are
 * "the two that change state" (task-5-brief), never a no-op "you're already this". Also backs
 * the bulk bar's own "Mark contacted"/"Archive" buttons below — same action, same word,
 * triggered from a second control on this same screen. */
function statusActions(t: Catalog): { status: LeadStatus; label: string }[] {
  return [
    { status: 'new', label: t.leads.actionMarkNew },
    { status: 'contacted', label: t.leads.actionMarkContacted },
    { status: 'archived', label: t.leads.actionArchive },
  ];
}

function statusLabel(status: LeadStatus, t: Catalog): string {
  switch (status) {
    case 'new':
      return t.leads.statusNew;
    case 'contacted':
      return t.leads.statusContacted;
    case 'archived':
      return t.leads.statusArchived;
  }
}

const STATUS_BADGE_VARIANT: Record<LeadStatus, 'default' | 'secondary' | 'outline'> = {
  new: 'default',
  contacted: 'secondary',
  archived: 'outline',
};

/** Mirrors `leadBulkStatusSchema`'s `ids` cap (`packages/validation/src/leads.ts`) — the bulk
 * bar disables its own buttons rather than letting a selection past this size reach the
 * server and 400. */
const BULK_MAX = 200;

/** Segmented-control idiom shared with `BotUnanswered.tsx`'s tabs / `SourceDetail.tsx`'s
 * "Near-empty only" toggle / `BotSources.tsx`'s add-source tab strip: `aria-pressed`, filled
 * primary when active, muted hover otherwise. */
function FilterButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      }`}
    >
      {label}
      {count != null && ` (${count})`}
    </button>
  );
}

export function BotLeads() {
  const t = useT();
  const FILTER_TABS = filterTabs(t);
  const STATUS_ACTIONS = statusActions(t);
  const { botId } = useParams<{ botId: string }>();
  const queryClient = useQueryClient();

  const { data: bot } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });

  // Reads the same Overview aggregate the rail's `CountPill` does (`totals.newLeads`) — both
  // the filter's default tab and its "(n)" count come from here, not from a locally fetched
  // list length, so they agree with the rail regardless of which tab is active.
  const overviewQuery = useBotOverview(botId);
  const overview = overviewQuery.data;
  // Fix round 1, item 2 — gates `useLeads` until Overview has settled (resolved OR errored,
  // never gated forever on a failed Overview fetch) so a cold load fetches ONE `status` — the
  // real default — instead of `all` first and then `new` the instant Overview resolves.
  const overviewSettled = overviewQuery.isSuccess || overviewQuery.isError;
  const [filterOverride, setFilterOverride] = useState<LeadsFilter | null>(null);
  const defaultFilter: LeadsFilter = overview && overview.totals.newLeads > 0 ? 'new' : 'all';
  const filter = filterOverride ?? defaultFilter;

  const { data, isPending, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useLeads(botId, filter, {
    enabled: overviewSettled,
  });
  const items = data?.pages.flatMap((p) => p.items) ?? [];

  function invalidateLeads() {
    // Fix-wave item 3: a lead delete moves `totals.leads` and `totals.leadsThisWeek` on
    // Overview too — shared by both the single- and bulk-delete mutations below. Invalidating
    // the bare `['bots', botId, 'leads']` prefix (no filter suffix) matches every filter tab's
    // own cache entry, same partial-match TanStack Query already does elsewhere in this repo.
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'leads'] }),
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] }),
    ]);
  }

  // Single-lead delete: one shared mutation + the row currently targeted, since only one
  // confirm dialog is ever open at a time.
  const [deleteTarget, setDeleteTarget] = useState<LeadListItemDto | null>(null);
  const deleteLeadMutation = useMutation({
    mutationFn: (leadId: string) =>
      apiFetch<{ deleted: boolean }>(`/api/bots/${botId}/leads/${leadId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidateLeads();
      setDeleteTarget(null);
    },
  });

  // Bulk delete: type-to-confirm on the bot's name, mirroring BotSettings' DangerZone.
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const deleteAllMutation = useMutation({
    mutationFn: () => apiFetch<{ deleted: number }>(`/api/bots/${botId}/leads`, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidateLeads();
      setDeleteAllOpen(false);
    },
  });

  function openDeleteAll() {
    deleteAllMutation.reset();
    setConfirmName('');
    setDeleteAllOpen(true);
  }

  // Row status action (Mark contacted / Archive / Mark new). One shared mutation, tracked
  // per-row via `statusPendingId` — same pattern `BotUnanswered.tsx`'s Dismiss/Reopen uses —
  // since only one row action is realistically in flight at a time, but nothing stops two
  // clicks in quick succession from racing.
  const setStatusMutation = useSetLeadStatus(botId);
  const [statusPendingId, setStatusPendingId] = useState<string | null>(null);
  function setLeadStatus(leadId: string, status: LeadStatus) {
    setStatusPendingId(leadId);
    setStatusMutation.mutate({ leadId, status }, { onSettled: () => setStatusPendingId(null) });
  }

  // Selection + bulk bar. Scoped to whatever's currently loaded for the active filter (every
  // page fetched so far via "Load more") — there's no server-side "page" boundary in a
  // keyset-paginated list, so "select all (current page)" means everything on screen right
  // now. Cleared on a filter switch: a selection made under "New" doesn't silently carry into
  // "All" and get bulk-archived by mistake.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Fix round 1, item 1 — the EFFECTIVE selection, never the raw `selectedIds` Set directly.
  // A lead can leave `items` between one render and the next two ways: its own row action
  // changed its status and the active filter dropped it, or any other refetch reshuffled the
  // page — either way the raw Set can still be pointing at an id that's no longer on screen.
  // Every user-facing read (the "N selected" count, select-all's checked/indeterminate state,
  // the bulk POST body) goes through this intersection so none of them can ever reference a
  // lead that already left the list.
  const effectiveSelectedIds = new Set(items.map((lead) => lead.id).filter((id) => selectedIds.has(id)));
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const allSelected = items.length > 0 && effectiveSelectedIds.size === items.length;
  const someSelected = effectiveSelectedIds.size > 0 && !allSelected;
  // `indeterminate` has no JSX prop — it's an imperative-only DOM property (react-hooks/refs
  // forbids reading/writing a ref during render, so this can't be a plain `if` in the render
  // body the way a class component's `componentDidUpdate` could get away with).
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  function switchFilter(next: LeadsFilter) {
    setFilterOverride(next);
    setSelectedIds(new Set());
  }

  function toggleOne(leadId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(items.map((lead) => lead.id)));
  }

  const bulkMutation = useBulkSetLeadStatus(botId);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const bulkMessageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Same cleanup shape `FeedbackDialog.tsx`'s auto-close timer uses — clears the pending
  // `setBulkMessage(null)` on unmount so navigating away from the screen inside the 4s window
  // doesn't fire a state update against an unmounted component.
  useEffect(() => () => {
    if (bulkMessageTimer.current) clearTimeout(bulkMessageTimer.current);
  }, []);
  const bulkOverCap = effectiveSelectedIds.size > BULK_MAX;
  function bulkSetStatus(status: 'contacted' | 'archived') {
    if (bulkOverCap) return;
    const ids = Array.from(effectiveSelectedIds);
    bulkMutation.mutate(
      { ids, status },
      {
        onSuccess: ({ updated }) => {
          // `{ updated }` counts CHANGED rows only — never render "N of M" (task-5-brief).
          setBulkMessage(t.leads.bulkUpdatedMessage({ n: updated }));
          setSelectedIds(new Set());
          if (bulkMessageTimer.current) clearTimeout(bulkMessageTimer.current);
          bulkMessageTimer.current = setTimeout(() => setBulkMessage(null), 4000);
        },
      },
    );
  }

  // CSV export (H2 fix, final-fix round) — was a plain same-origin `<a href>`: a browser
  // navigation carries only cookies, never `apiFetch`'s custom headers, so a user working in a
  // non-default workspace (via `X-Workspace-Id`, set by `WorkspaceMenu`/`InviteAccept`)
  // had that context silently dropped and the request resolved against their FIRST membership
  // instead — 404 for anyone whose active workspace isn't the one they signed up with. Fetching
  // the CSV manually (not through `apiFetch`, which assumes a JSON body) and handing the
  // response to the browser as a blob download preserves the workspace header a navigation
  // can't carry.
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportCsvError, setExportCsvError] = useState<string | null>(null);
  async function handleExportCsv() {
    if (!botId || exportingCsv) return;
    setExportingCsv(true);
    setExportCsvError(null);
    try {
      const res = await fetch(`/api/bots/${botId}/leads.csv`, {
        credentials: 'include',
        headers: withWorkspaceHeader(),
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      // Keep the filename the server sends (`leads-{publicId}-{date}.csv`, `routes/leads.ts`)
      // rather than hardcoding it here, falling back only if the header is ever missing.
      const disposition = res.headers.get('Content-Disposition');
      const filename = disposition?.match(/filename="?([^"]+)"?/)?.[1] ?? 'leads.csv';
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportCsvError(t.leads.exportCsvError);
    } finally {
      setExportingCsv(false);
    }
  }

  // Header subline describes the rows on screen — `overview.totals.leads` is the wrong source
  // here: it is a VISITOR figure that excludes playground-captured leads, while this list is the
  // audit surface that keeps and labels them (re-review 2026-08-21: a bot whose leads were all
  // captured in the Playground read "0 leads captured" above visible rows). On the All filter the
  // loaded count is the bot's count; on a status filter the line names the filter so it never
  // reads as the bot's total while a near-empty tab is open.
  // Structurally-identical Skeleton while loading (via `PageHeader`'s `subtitleLoading`,
  // subtitle-skeleton spec §C, 2026-08-22) so the filter tabs never jump (Johnny, 2026-08-22).
  // The active filter's own label, lowercased — shared by the header subline and the
  // filtered-empty-state title below so the two never phrase the same filter two ways.
  const filterLabel = (FILTER_TABS.find((tab) => tab.id === filter)?.label ?? filter).toLowerCase();
  const headerSubtitle = error
    ? undefined
    : filter === 'all'
      ? t.leads.headerSubtitleAll({ n: items.length })
      : t.leads.headerSubtitleFiltered({ n: items.length, filterLabel });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t.leads.pageTitle}
        subtitle={headerSubtitle}
        subtitleLoading={isPending}
        action={
          <button
            type="button"
            disabled={exportingCsv}
            onClick={() => void handleExportCsv()}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            {exportingCsv ? t.leads.exportingCta : t.leads.exportCsvCta}
          </button>
        }
      />
      {exportCsvError && <p className="text-sm text-destructive">{exportCsvError}</p>}

      <div className="flex gap-1" role="group" aria-label={t.leads.filterByStatusAriaLabel}>
        {FILTER_TABS.map((tab) => (
          <FilterButton
            key={tab.id}
            label={tab.label}
            count={tab.id === 'new' ? overview?.totals.newLeads : undefined}
            active={filter === tab.id}
            onClick={() => switchFilter(tab.id)}
          />
        ))}
      </div>

      {isPending && <TableSkeleton />}
      {error && <p className="text-destructive">{error.message}</p>}

      {!isPending && !error && (
        <div className="flex flex-col gap-4">
          {/* Any lead on the bot — loaded rows (incl. playground) OR the visitor total — since the
              action deletes every lead regardless of channel or filter. */}
          {(items.length > 0 || (overview?.totals.leads ?? 0) > 0) && bot && (
            <div className="flex justify-end">
              <Button type="button" variant="destructive-soft" size="sm" onClick={openDeleteAll}>
                {t.leads.deleteAllCta}
              </Button>
            </div>
          )}

          {effectiveSelectedIds.size > 0 && (
            <div
              role="toolbar"
              aria-label={t.leads.bulkActionsAriaLabel}
              className="flex items-center justify-between gap-3 rounded-lg border bg-muted px-4 py-2.5"
            >
              <span className="text-[13px] font-medium">
                {t.leads.selectedCount({ n: effectiveSelectedIds.size })}
              </span>
              <div className="flex items-center gap-2">
                {bulkOverCap && (
                  <span className="text-[12.5px] text-muted-foreground">
                    {t.leads.bulkCapMessage({ max: BULK_MAX })}
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={bulkMutation.isPending || bulkOverCap}
                  onClick={() => bulkSetStatus('contacted')}
                >
                  {t.leads.actionMarkContacted}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={bulkMutation.isPending || bulkOverCap}
                  onClick={() => bulkSetStatus('archived')}
                >
                  {t.leads.actionArchive}
                </Button>
              </div>
            </div>
          )}
          {bulkMessage && <p className="text-[13px] text-muted-foreground">{bulkMessage}</p>}
          {bulkMutation.isError && (
            // Friendly, generic copy (fix round 1, item 3) — not `bulkMutation.error.message`;
            // details of what went wrong belong in server logs, same voice
            // `BotUnanswered.tsx`'s Write-answer error already uses.
            <p className="text-[13px] text-destructive">{t.leads.bulkUpdateError}</p>
          )}

          {items.length === 0 ? (
            filter === 'all' ? (
              <EmptyState
                icon={InboxIcon}
                title={t.leads.emptyTitle}
                description={t.leads.emptyDescription}
              />
            ) : (
              <EmptyState
                icon={InboxIcon}
                title={t.leads.noFilterResultsTitle({ filterLabel })}
                description={t.leads.noFilterResultsDescription}
              />
            )
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      aria-label={t.leads.selectAllAriaLabel}
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="size-4 rounded accent-[var(--primary)]"
                    />
                  </TableHead>
                  <TableHead>{t.leads.colName}</TableHead>
                  <TableHead>{t.leads.colContact}</TableHead>
                  <TableHead>{t.leads.colIntent}</TableHead>
                  <TableHead>{t.leads.colStatus}</TableHead>
                  <TableHead>{t.leads.colCaptured}</TableHead>
                  <TableHead>{t.leads.colActions}</TableHead>
                  <TableHead />
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((lead) => (
                  <TableRow key={lead.id} data-state={effectiveSelectedIds.has(lead.id) ? 'selected' : undefined}>
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={t.leads.selectLeadAriaLabel({
                          contact: lead.email || lead.phone || t.leads.noEmailFallback,
                        })}
                        checked={effectiveSelectedIds.has(lead.id)}
                        onChange={() => toggleOne(lead.id)}
                        className="size-4 rounded accent-[var(--primary)]"
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-1.5">
                        {lead.name ?? <span className="text-muted-foreground">—</span>}
                        {lead.playgroundCaptured && (
                          // Fix-wave item 5: Overview's "Leads captured" stat excludes these
                          // (they're the owner's own testing, not a visitor), but this list is
                          // the audit surface — it keeps every lead and labels this one apart
                          // rather than hiding it, same resolution as Conversations' "Your
                          // testing" / "Playground" tag.
                          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
                            {t.leads.playgroundTag}
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {lead.email}
                      {/* Lead-phone-capture design (2026-08-22): a muted second line in the SAME
                          cell, not a new column — the table already carries eight of them, and
                          email and phone are one fact ("how to reach this person"). Empty stays
                          empty: a lead with no phone renders exactly what it rendered before. */}
                      {lead.phone ? <span className="block text-xs">{lead.phone}</span> : null}
                    </TableCell>
                    <TableCell className="max-w-80 truncate" title={lead.intent ?? undefined}>
                      {lead.intent ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE_VARIANT[lead.status]}>{statusLabel(lead.status, t)}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{relativeTime(lead.createdAt, t.time)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {STATUS_ACTIONS.filter((a) => a.status !== lead.status).map((a) => (
                          <button
                            key={a.status}
                            type="button"
                            disabled={statusPendingId === lead.id}
                            onClick={() => setLeadStatus(lead.id, a.status)}
                            className="text-[12.5px] font-medium text-primary hover:underline disabled:pointer-events-none disabled:opacity-50"
                          >
                            {a.label}
                          </button>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {lead.conversationId ? (
                        <Link
                          to={`/bots/${botId}/conversations?open=${lead.conversationId}`}
                          className="text-[12.5px] font-medium text-primary hover:underline"
                        >
                          {t.leads.viewChatCta}
                        </Link>
                      ) : (
                        // Pre-chat leads (spec §1) never attach to a conversation — the form
                        // is submitted before the chat starts, so there's nothing to open.
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        aria-label={t.leads.deleteLeadAriaLabel({
                          contact: lead.email || lead.phone || t.leads.noEmailFallback,
                        })}
                        onClick={() => {
                          deleteLeadMutation.reset();
                          setDeleteTarget(lead);
                        }}
                        className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-destructive focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
                      >
                        <Trash2Icon className="size-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {hasNextPage && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? t.leads.loadingMoreCta : t.leads.loadMoreCta}
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.leads.deleteDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.leads.deleteDescriptionPrefix}
              <span className="font-medium text-foreground">
                {deleteTarget?.email || deleteTarget?.phone || t.leads.noContactFallback}
              </span>
              {t.leads.deleteDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          {deleteLeadMutation.isError && (
            <p className="text-[13px] text-destructive">{deleteLeadMutation.error.message}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)}>
              {t.leads.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteLeadMutation.isPending}
              onClick={() => deleteTarget && deleteLeadMutation.mutate(deleteTarget.id)}
            >
              {deleteLeadMutation.isPending ? t.leads.deletingCta : t.leads.deleteCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.leads.deleteAllDialogTitle}</DialogTitle>
            <DialogDescription>
              {t.leads.deleteAllDescriptionPrefix}
              <span className="font-medium text-foreground">{bot?.name}</span>
              {t.leads.deleteAllDescriptionSuffix}
            </DialogDescription>
          </DialogHeader>
          <Input
            aria-label={t.leads.confirmBotNameAriaLabel}
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={bot?.name}
            autoFocus
          />
          {deleteAllMutation.isError && (
            <p className="text-[13px] text-destructive">{deleteAllMutation.error.message}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteAllOpen(false)}>
              {t.leads.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={confirmName !== bot?.name || deleteAllMutation.isPending}
              onClick={() => deleteAllMutation.mutate()}
            >
              {deleteAllMutation.isPending ? t.leads.deletingCta : t.leads.deleteAllCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
